<template>
  <div class="ts-p-3">
    <div class="ts-overflow-y-scroll max-h">
      <ExcelTemplate v-model="model" :validate="errors" />
      <table class="ts-w-full">
        <thead>
          <tr>
            <th class="ts-text-center" style="width: 1%">
              {{ $t('no') }}
            </th>
            <th style="width: 10%">
              {{ $t('purchaseOrder.barcode') }}
            </th>
            <th style="width: 15%">
              {{ $t('purchaseOrder.productName') }}
            </th>
            <th style="width: 10%">
              {{ $t('purchaseOrder.uom') }}
            </th>
            <th class="ts-text-center" style="width: 8%">
              {{ $t('purchaseOrder.quantity') }}
            </th>
            <th class="ts-text-center" style="width: 10%">
              {{ $t('purchaseOrder.cost') }}
            </th>
            <th style="width: 10%">
              {{ $t('purchaseOrder.discount') }}
            </th>
            <th style="width: 8%">
              {{ $t('purchaseOrder.disType') }}
            </th>
            <th class="ts-text-center" style="width: 10%">
              {{ $t('purchaseOrder.amount') }}
            </th>
            <th class="ts-text-center" style="width: 10%">
              {{ $t('purchaseOrder.convertedAmount') }}
            </th>
            <th class="ts-text-center" style="width: 10%">
              {{ $t('purchaseOrder.maxReorder') }}
            </th>
            <th class="ts-text-center" style="width: 10%">
              {{ $t('purchaseOrder.alloworder') }}
            </th>
            <th class="ts-text-center" style="width: 3%">
              <a href="#" class="ts-text-white" @click.prevent="addNewRow">
                <i class="fa fa-plus"></i>
              </a>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="model.po_detail == 0">
            <td colspan="13" class="ts-text-center ts-p-3 ts-space-x-1">
              <i class="fa fa-folder-open ts-text-gray-200"></i>
              <label class="ts-text-gray-400">
                {{ $t('noData') }}
              </label>
              <div class="validate ts-text-left" v-if="errors.has('po_detail')">
                {{ errors.first('po_detail') }}
              </div>
            </td>
          </tr>
          <tr v-for="(d, index) in model.po_detail" :key="index">
            <td class="ts-text-center">
              {{ index + 1 }}
            </td>
            <td>
              <input
                type="text"
                class="form-control"
                style="min-width: 50px"
                :value="
                  findProduct(d.product_id)
                    ? findProduct(d.product_id).barcode
                    : ''
                "
                readonly
              />
            </td>
            <td>
              <input
                type="text"
                :value="
                  findProduct(d.product_id)
                    ? findProduct(d.product_id).product_name_en
                    : ''
                "
                class="form-control"
                readonly
                style="min-width: 200px"
              />
            </td>
            <td>
              <a-select
                v-model="d.uom_id"
                class="ts-w-full"
                style="min-width: 70px"
                :options="getUnitOfMeasures(d.uom_cat_id)"
                option-filter-prop="children"
                :class="{
                  'is-invalid': errors.has('po_detail.' + index + '.uom_id')
                }"
                @change="onUomChange(d)"
              >
              </a-select>
            </td>
            <td>
              <input
                v-model.number="d.qty"
                type="number"
                ref="qty"
                class="form-control ts-text-center"
                :class="{
                  'is-invalid': errors.has('po_detail.' + index + '.qty')
                }"
                style="min-width: 70px"
              />
              <div
                class="validate"
                v-if="errors.has('po_detail.' + index + '.qty')"
              >
                {{ errors.first('po_detail.' + index + '.qty') }}
              </div>
            </td>
            <td>
              <ts-currency-converter-input
                :input_amount.sync="d.input_cost"
                :amount.sync="d.cost"
                :input_currency_id.sync="d.input_currency_id"
                :currency_id.sync="d.currency_id"
                :fx_rate.sync="d.fx_rate"
                :fx_rate_ratio.sync="d.fx_rate_ratio"
                style="min-width: 120px;"
              />
              <div
                class="validate"
                v-if="errors.has('po_detail.' + index + '.cost')"
              >
                {{ errors.first('po_detail.' + index + '.cost') }}
              </div>
            </td>
            <td>
              <input
                v-if="d.discount_type == '%'"
                ref="discountRate"
                type="number"
                v-model.number="d.input_discount_rate"
                class="form-control"
                :class="{
                  'is-invalid': errors.has(
                    'po_detail.' + index + '.discount_rate'
                  )
                }"
              />
              <ts-currency-converter-input
                v-else
                :input_amount.sync="d.input_discount_rate"
                :amount.sync="d.discount_rate"
                :input_currency_id.sync="d.input_currency_id"
                :currency_id.sync="d.currency_id"
                :fx_rate.sync="d.fx_rate"
                :fx_rate_ratio.sync="d.fx_rate_ratio"
                style="min-width: 120px;"
              />
              <div
                class="invalid-feedback"
                v-if="errors.has('po_detail.' + index + '.discount_rate')"
              >
                {{ errors.first('po_detail.' + index + '.discount_rate') }}
              </div>
            </td>
            <td>
              <a-select
                v-model="d.discount_type"
                class="ts-w-full"
                style="min-width: 50px"
                :class="{
                  'is-invalid': errors.has(
                    'po_detail.' + index + '.discount_type'
                  )
                }"
              >
                <a-select-option value="%">%</a-select-option>
                <a-select-option
                  :value="getDiscountCurrency(d.input_currency_id)"
                >
                  {{ getDiscountCurrency(d.input_currency_id) }}
                </a-select-option>
              </a-select>
              <div
                class="invalid-feedback"
                v-if="errors.has('po_detail.' + index + '.discount_type')"
              >
                {{ errors.first('po_detail.' + index + '.discount_type') }}
              </div>
            </td>
            <td>
              <ts-currency-input
                v-model.number="d.input_total_after_discount"
                class="form-control ts-text-right"
                style="min-width: 120px"
                :currency="getCurrencySign(d.input_currency_id)"
                :precision="getPrecision(d.input_currency_id)"
                :disabled="true"
              />
            </td>
            <td>
              <ts-currency-input
                v-model.number="d.total_after_discount"
                class="form-control ts-text-right"
                style="min-width: 120px"
                :currency="getCurrencySign(d.currency_id)"
                :precision="getPrecision(d.currency_id)"
                :disabled="true"
              />
            </td>
            <td>
              <input
                v-model="d.maximum_reorder_allow"
                type="text"
                class="form-control ts-text-center"
                style="min-width: 90px"
                readonly
              />
              <div
                class="validate"
                v-if="
                  errors.has('po_detail.' + index + '.maximum_reorder_allow')
                "
              >
                {{
                  errors.first('po_detail.' + index + '.maximum_reorder_allow')
                }}
              </div>
            </td>
            <td style="width: 1%" class="ts-text-center">
              <input type="checkbox" v-model="d.alloworder" />
            </td>
            <td style="min-width: 35px" class="ts-text-center">
              <a
                href="#"
                class="ts-text-red-700 hover:ts-text-red-700"
                @click.prevent="removeItem(index)"
              >
                <i class="fa fa-trash"></i>
              </a>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="total-footer">
            <td colspan="2">
              <b>
                {{ $t('purchaseOrder.numberRow') }} :
                {{ model.po_detail.length }}
              </b>
            </td>
            <td colspan="2" class="ts-text-right">
              <b>{{ $t('purchaseOrder.total') }} :</b>
            </td>
            <td>
              <input
                v-model.number="sumTotalQty"
                type="number"
                class="form-control ts-text-center"
                style="min-width: 100px;border:transparent;"
                readonly
              />
            </td>
            <td colspan="4"></td>
            <td>
              <ts-currency-input
                v-model.number="sumTotalAfterDiscount"
                class="form-control ts-text-right ts-font-bold"
                style="min-width: 100px;border:transparent;"
                :currency="defaultCurrency.currency_symbol"
                :precision="defaultCurrency.currency_rounding"
                :disabled="true"
              />
            </td>
            <td colspan="3"></td>
          </tr>
        </tfoot>
      </table>

      <!-- modal show pop-up to be able add product -->
      <a-modal
        v-if="show_modal_add_product"
        v-model="show_modal_add_product"
        :footer="null"
        :zIndex="1020"
        :maskClosable="false"
        width="900px"
        :title="$t('stockIn.product')"
      >
        <ProductList v-model="model" />
      </a-modal>
    </div>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapState } from 'vuex'
import ProductList from './product-list.vue'
import ExcelTemplate from './excel-template.vue'
import { sumBy } from 'lodash'

export default {
  name: 'purchaseOrderItem',
  components: {
    ExcelTemplate,
    ProductList
  },
  props: ['value', 'validate'],
  data() {
    return {
      errors: new Errors(),
      show_modal_add_product: false,
      model: {}
    }
  },
  computed: {
    ...mapState('purchaseOrderV2/order', ['edit_data', 'formViewDatas']),
    products() {
      if (this.formViewDatas['products']) {
        return this.formViewDatas['products']
      }
      return []
    },
    unitOfMeasures() {
      if (this.formViewDatas['unitOfMeasures']) {
        return this.formViewDatas['unitOfMeasures'].map(el => ({
          value: el.uom_id,
          label: el.uom_name_en,
          uom_cat_id: el.uom_cat_id,
          is_default: el.is_default,
          stockout_quantity: el.stockout_quantity
        }))
      }
      return []
    },
    defaultCurrency() {
      if (this.formViewDatas['currency']) {
        return this.formViewDatas['currency'].find(el => el.is_default == true)
      }
      return {
        currency_symbol: '',
        currency_rounding: null
      }
    },
    sumTotalQty() {
      return sumBy(this.model.po_detail, 'qty')
    },
    sumTotalAfterDiscount() {
      return sumBy(this.model.po_detail, 'total_after_discount')
    },
    isEdit() {
      return Object.keys(this.edit_data).length
    }
  },
  created() {
    this.model = this.value
  },
  methods: {
    findProduct(product_id) {
      return this.products.find(el => el.product_id == product_id)
    },
    getUnitOfMeasures(uom_cat_id) {
      return this.unitOfMeasures.filter(el => el.uom_cat_id == uom_cat_id)
    },
    getDiscountCurrency(input_currency_id) {
      if (this.formViewDatas['currency']) {
        return this.formViewDatas['currency'].find(
          el => el.currency_id == input_currency_id
        ).currency_symbol
      }
      return null
    },
    getCurrencySign(currency_id) {
      if (this.formViewDatas['currency']) {
        const currency = this.formViewDatas['currency'].find(
          el => el.currency_id == currency_id
        )
        return currency ? currency.currency_symbol : null
      }
      return null
    },
    getPrecision(currency_id) {
      if (this.formViewDatas['currency']) {
        const currency = this.formViewDatas['currency'].find(
          el => el.currency_id == currency_id
        )
        return currency ? currency.currency_rounding : null
      }
      return null
    },
    addNewRow() {
      this.show_modal_add_product = true
    },
    removeItem(index) {
      this.model.po_detail.splice(index, 1)
    },
    onUomChange(row) {
      const product = this.products.find(el => el.product_id == row.product_id)
      const price = product.sale_code.find(el => el.uom_id == row.uom_id)

      row.cost = price ? price.cost : 0
      row.input_cost = price ? price.cost : 0
      row.input_currency_id = row.currency_id
    },
    calculate() {
      this.model.po_detail.forEach(el => {
        if (el.discount_type == '%') {
          el.discount_rate = el.input_discount_rate

          let input_discount =
            el.input_cost * el.qty * (el.input_discount_rate / 100)

          el.input_total_after_discount =
            el.input_cost * el.qty - input_discount
          el.total_after_discount =
            el.input_total_after_discount * el.fx_rate_ratio
        } else {
          el.discount_type = this.getDiscountCurrency(el.input_currency_id)

          el.discount_rate = el.input_discount_rate * el.fx_rate_ratio

          el.input_total_after_discount =
            el.input_cost * el.qty - el.input_discount_rate

          el.total_after_discount =
            el.input_total_after_discount * el.fx_rate_ratio
        }
      })
    }
  },
  watch: {
    'model.po_detail': {
      handler() {
        this.calculate()
      },
      deep: true
    },
    validate: function(error) {
      this.errors = new Errors(error.errors)
    }
  }
}
</script>

<style scoped>
.form-control[disabled],
.form-control[readonly],
fieldset[disabled] .form-control {
  opacity: 1;
}
.py {
  padding: 0.45rem 0;
}
.max-h {
  min-height: 30vh;
  max-height: 50vh;
}
table {
  position: relative;
  border-collapse: collapse;
}
table thead tr th {
  padding: 4px 5px;
  z-index: 3;
  position: sticky;
  top: 0; /* Don't forget this, required for the stickiness */
  background-color: rgb(22, 102, 145);
  color: white;
  text-transform: uppercase;
  border-width: 1px;
  white-space: nowrap;
}

table tbody tr td {
  padding: 4px 5px;
  border-width: 1px;
}

table tfoot tr {
  background-color: #e3e8f1;
  border-width: 1px;
}

table tfoot tr td {
  padding: 0 5px;
  white-space: nowrap;
  color: #c53030;
}

.total-footer .form-control {
  font-weight: 900;
  color: #c53030;
}
</style>
